import { z } from "zod";

// ─── Per-section zod schemas (mirroring §10.5 of the requirements doc) ─────

export const heroSchema = z.object({
  headline: z.string().trim().min(1, "Required").max(300),
  subtext: z.string().trim().min(1, "Required").max(1500),
  eyebrow: z.string().trim().max(200).optional().or(z.literal("")),
});

export const heroImageSchema = z.object({
  image_s3_key: z.string().trim().max(512).optional().or(z.literal("")),
  // Read-only — computed by the backend from `image_s3_key`. Kept in the
  // schema so RHF can carry it through the edit cycle for previews; the
  // upsert call must strip it before sending (see HeroImageEditor).
  image_url: z.string().trim().optional().or(z.literal("")),
});

export const whatThisIsSchema = z.object({
  title: z.string().trim().max(200).default("What this is"),
  description: z.string().trim().min(1, "Required").max(2000),
  pill_chips: z.array(z.string().trim().min(1)).max(10).default([]),
});

export const whenToUseSchema = z.object({
  title: z.string().trim().max(200).default("When you might use it"),
  description: z.string().trim().min(1, "Required").max(2000),
  items: z
    .array(
      z.object({
        icon: z.string().trim().optional().or(z.literal("")),
        lead_in: z.string().trim().min(1, "Required").max(200),
        description: z.string().trim().min(1, "Required").max(500),
      })
    )
    .max(12)
    .default([]),
});

export const whereItFitsSchema = z.object({
  title: z.string().trim().max(200).default("Where it fits"),
  description: z.string().trim().max(1000).optional().or(z.literal("")),
  cards: z
    .array(
      z.object({
        title: z.string().trim().min(1, "Required").max(200),
        subtitle: z.string().trim().max(300).optional().or(z.literal("")),
        icon: z.string().trim().optional().or(z.literal("")),
      })
    )
    .max(8)
    .default([]),
});

export const privateByDesignSchema = z.object({
  title: z.string().trim().max(200).default("Private by design"),
  description: z.string().trim().max(1000).optional().or(z.literal("")),
  bullets: z.array(z.string().trim().min(1)).max(8).default([]),
});

export const closingTaglineSchema = z.object({
  text: z.string().trim().min(1, "Required").max(1000),
});

export const footerSchema = z.object({
  sponsor_line: z.string().trim().min(1, "Required").max(300),
  copyright_line: z.string().trim().max(300).optional().or(z.literal("")),
});

export const ctaSchema = z.object({
  card_heading: z.string().trim().min(1, "Required").max(200),
  card_subtitle: z.string().trim().max(500).optional().or(z.literal("")),
  button_label: z.string().trim().min(1, "Required").max(60).default("Activate Access"),
});

export const stepLabelsSchema = z.object({
  labels: z
    .array(z.string().trim().min(1).max(40))
    .min(1, "At least one label")
    .max(8),
});

// ─── Default values for "Not yet authored" sections ────────────────────────

export const SECTION_DEFAULTS = {
  hero: {
    headline: "Think *clearly* in the moments that matter",
    subtext: "",
    eyebrow: "",
  },
  hero_image: {
    image_s3_key: "",
    image_url: "",
  },
  what_this_is: {
    title: "What this is",
    description: "",
    pill_chips: ["Voice", "Text"],
  },
  when_to_use: {
    title: "When you might use it",
    description: "",
    items: [],
  },
  where_it_fits: {
    title: "Where it fits",
    description: "",
    cards: [],
  },
  private_by_design: {
    title: "Private by design",
    description: "",
    bullets: [],
  },
  closing_tagline: { text: "" },
  footer: { sponsor_line: "", copyright_line: "© 2026 Aris" },
  cta: {
    card_heading: "Activate Your Access",
    card_subtitle: "",
    button_label: "Activate Access",
  },
  step_labels: { labels: ["Verify", "Set up", "Start using"] },
};

export const SECTION_DISPLAY_NAMES = {
  hero_image: "Hero image",
  hero: "Hero",
  what_this_is: "What this is",
  when_to_use: "When you might use it",
  where_it_fits: "Where it fits",
  private_by_design: "Private by design",
  closing_tagline: "Closing tagline",
  footer: "Footer",
  cta: "CTA card",
  step_labels: "Stepper labels",
};

export const SCHEMA_BY_KEY = {
  hero_image: heroImageSchema,
  hero: heroSchema,
  what_this_is: whatThisIsSchema,
  when_to_use: whenToUseSchema,
  where_it_fits: whereItFitsSchema,
  private_by_design: privateByDesignSchema,
  closing_tagline: closingTaglineSchema,
  footer: footerSchema,
  cta: ctaSchema,
  step_labels: stepLabelsSchema,
};
